import { graphLinks, subjectDefinition } from "./authoring-subject.mjs";
import { TRIPROUTES_AGENT_CARDS } from "./triproutes-agents.mjs";

const cards = new Map(TRIPROUTES_AGENT_CARDS.map(card => [card.id, card]));
const services = new Set(["pipeline_source", "city_source", "route_file_source", "normalize", "native_apply", "poi_prepare", "poi_topup_1", "poi_topup_2", "bp2_media", "place_scope", "public_response"]);

// Group only registered editor definitions, never infer an agent from a user-
// editable name or the default agentId attached to native operations.
export function agentForNode(node, context) {
  const definition = subjectDefinition(node, context);
  if (!definition?.modelSlots?.length) return null;
  const template = definition.nodeTemplate;
  const id = template?.id || node.id;
  let key = template?.parameters?.agentId?.replace(/^triproutes-/, "");
  if (key === "city-content" && id === "route_sources") key = "city-research";
  if (["query-parse", "idea-suggest", "route-expand"].includes(key)) key = `user-ai-${key.replaceAll("-", "_")}`;
  const card = cards.get(key);
  return card ? { key, label: card.name, description: key === "route-ideas" && context.parallelIdeas ? "Независимые генераторы предлагают идеи маршрутов. Общий отбор сравнивает все полученные варианты." : card.description, optional: key === "city-content" && context.inputAdapter?.kind === "triproutes-city-routes" } : null;
}

export function serviceNode(node, context) {
  const definition = subjectDefinition(node, context);
  return Boolean(definition && !definition.modelSlots?.length && services.has(definition.nodeTemplate?.id || node.id));
}

export function stageModels(node, context) {
  const definition = subjectDefinition(node, context);
  return (definition?.modelSlots || []).map(slot => {
    const ref = node.modelSlots?.[slot.id];
    return slot.choices?.find(choice => choice.choiceRef === ref)?.label || (ref === slot.currentChoiceRef ? slot.currentLabel : "") || "Модель не выбрана";
  });
}

export function agentGraph(document, context, { expanded = [] } = {}) {
  const groups = [], items = [], owner = new Map(), expandedSet = new Set(expanded);
  let previous = null;
  const used = new Set(document.nodes.map(node => node.id));
  for (const node of document.nodes) {
    const agent = agentForNode(node, context);
    if (agent) {
      // Separate disjoint occurrences: merging A → B → A would invent a cycle.
      if (previous?.key !== agent.key) {
        let id = `@agent:${agent.key}:${groups.length}`;
        while (used.has(id)) id += ":group";
        used.add(id);
        previous = { ...agent, id, members: [] };
        groups.push(previous); items.push({ id, group: previous });
      }
      previous.members.push(node);
      owner.set(node.id, previous.id);
    } else if (!serviceNode(node, context)) {
      items.push({ id: node.id, node }); owner.set(node.id, node.id); previous = null;
    }
  }
  const visible = items.flatMap(item => item.group && expandedSet.has(item.id)
    ? item.group.members.map(node => { owner.set(node.id, node.id); return { id: node.id, node, parentGroup: item.group }; }) : [item]);
  const links = graphLinks(document), outgoing = new Map();
  for (const link of links) outgoing.set(link.source, [...(outgoing.get(link.source) || []), link.target]);
  const edges = new Map();
  for (const node of document.nodes) {
    const from = owner.get(node.id);
    if (!from) continue;
    const visited = new Set([node.id]);
    const visit = (id, via, seen = visited) => {
      if (seen.has(id)) return;
      seen.add(id);
      const to = owner.get(id);
      if (to && to !== from) {
        const key = JSON.stringify([from, to]);
        const edge = edges.get(key) || { id: `view:${key}`, source: from, target: to, via: [] };
        edge.via = [...new Set([...edge.via, ...via])]; edges.set(key, edge);
      } else if (!to || to === from) {
        for (const target of outgoing.get(id) || []) visit(target, to ? via : [...via, id], seen);
      }
    };
    for (const target of outgoing.get(node.id) || []) visit(target, []);
  }
  return { items: visible, groups, edges: [...edges.values()], hiddenServices: document.nodes.filter(node => serviceNode(node, context)).length };
}

// Compact snake for a chain, layered layout for branches. Neither changes the
// document or its execution dependencies. Ports follow the actual visual row.
export function agentPositions(items, edges, columns = 3, measurements = {}) {
  const rowGap = Math.max(195, ...items.map(item => (measurements[item.id]?.height || 0) + 32));
  const incoming = new Map(items.map(item => [item.id, []])), outgoing = new Map(items.map(item => [item.id, []]));
  for (const edge of edges) { incoming.get(edge.target)?.push(edge.source); outgoing.get(edge.source)?.push(edge.target); }
  const ready = items.filter(item => !incoming.get(item.id).length).map(item => item.id), order = [], ranks = new Map(ready.map(id => [id, 0])), remaining = new Map([...incoming].map(([id, sources]) => [id, sources.length]));
  while (ready.length) {
    const id = ready.shift(); order.push(id);
    for (const target of outgoing.get(id)) {
      ranks.set(target, Math.max(ranks.get(target) || 0, ranks.get(id) + 1));
      remaining.set(target, remaining.get(target) - 1); if (!remaining.get(target)) ready.push(target);
    }
  }
  const chain = order.length === items.length && items.every(item => incoming.get(item.id).length <= 1 && outgoing.get(item.id).length <= 1) && edges.length === Math.max(0, items.length - 1);
  const result = {}, lanes = new Map();
  if (chain) order.forEach((id, index) => {
    const row = Math.floor(index / columns), lane = index % columns, reverse = row % 2 === 1;
    result[id] = { position: { x: 30 + (reverse ? columns - 1 - lane : lane) * 280, y: 90 + row * rowGap }, target: lane === 0 && row > 0 ? "top" : reverse ? "right" : "left", source: lane === columns - 1 ? "bottom" : reverse ? "left" : "right" };
  });
  else items.forEach(item => {
    const rank = ranks.get(item.id) || 0, lane = lanes.get(rank) || 0; lanes.set(rank, lane + 1);
    result[item.id] = { position: { x: 30 + lane * 280, y: 90 + rank * rowGap }, target: "top", source: "bottom" };
  });
  return result;
}
